import type { PublishedDraw, PublishedWinEvent } from "../../lotto-results";
import type { LottoStore } from "../../lotto-stores";
import { generateLottoPick, numberFrequency, rankFrequency, type NumberFrequency, type PickStrategy } from "./lotto-number-engine";

export type RecommendationScope = "NATIONWIDE" | "NEARBY_5" | "CURRENT_DISTRICT" | "CURRENT_PROVINCE";
export type RecommendationRank = "FIRST" | "SECOND" | "FIRST_AND_SECOND";
export type RecommendationPeriod = 10 | 30 | 100 | "ALL";
export type RecommendationMethod = "OVERALL_HOT" | "LOCAL_HOT" | "RECENT_SURGE" | "LONG_ABSENT" | "MIXED" | "BALANCED" | "RANDOM";

export interface BrowserLocation { readonly latitude: number; readonly longitude: number }

export interface RecommendationInput {
  readonly draws: readonly PublishedDraw[];
  readonly stores: readonly LottoStore[];
  readonly events: readonly PublishedWinEvent[] | null;
  readonly scope: RecommendationScope;
  readonly rank: RecommendationRank;
  readonly period: RecommendationPeriod;
  readonly method: RecommendationMethod;
  readonly generationIndex: number;
  readonly location: BrowserLocation | null;
}

export interface LottoRecommendation {
  readonly picks: readonly (readonly number[])[];
  readonly overallFrequency: readonly NumberFrequency[];
  readonly localFrequency: readonly NumberFrequency[];
  readonly recentFrequency: readonly NumberFrequency[];
  readonly periodDrawCount: number;
  readonly localDrawCount: number;
  readonly currentAreaLabel: string | null;
  readonly needsLocation: boolean;
  readonly needsWinningHistory: boolean;
}

export function recommendLottoNumbers(input: RecommendationInput): LottoRecommendation {
  const draws = [...input.draws].sort((left, right) => right.drawNo - left.drawNo);
  const periodDraws = input.period === "ALL" ? draws : draws.slice(0, input.period);
  const nearbyStore = input.location === null ? null : nearestStore(input.stores, input.location);
  const currentAreaLabel = areaLabel(input.scope, nearbyStore);
  const scopedStoreIds = storeIdsForScope(input.scope, input.stores, input.location, nearbyStore);
  const selectedRanks = input.rank === "FIRST" ? new Set([1]) : input.rank === "SECOND" ? new Set([2]) : new Set([1, 2]);
  const localDraws = input.events === null ? [] : localDrawsFor(periodDraws, input.events, scopedStoreIds, selectedRanks);
  const overallFrequency = rankFrequency(periodDraws);
  const localFrequency = rankFrequency(localDraws);
  const recentFrequency = rankFrequency(periodDraws.slice(0, 10));
  const weights = weightsFor(input.method, periodDraws, overallFrequency, localFrequency, recentFrequency);
  const strategy = strategyFor(input.method);
  const picks = Array.from({ length: 5 }, (_, index) => generateLottoPick(strategy, 645_000 + input.generationIndex * 31 + index, {}, weights));
  return {
    picks,
    overallFrequency,
    localFrequency,
    recentFrequency,
    periodDrawCount: periodDraws.length,
    localDrawCount: localDraws.length,
    currentAreaLabel,
    needsLocation: input.scope !== "NATIONWIDE" && input.location === null,
    needsWinningHistory: input.events === null,
  };
}

export function recommendationSummary(numbers: readonly number[], recentFrequency: readonly NumberFrequency[], localFrequency: readonly NumberFrequency[]): string {
  const oddCount = numbers.filter((number) => number % 2 === 1).length;
  const recentCounts = new Map(recentFrequency.map((item) => [item.number, item.count]));
  const localCounts = new Map(localFrequency.map((item) => [item.number, item.count]));
  const recentHits = numbers.filter((number) => (recentCounts.get(number) ?? 0) > 0).length;
  const localHits = numbers.filter((number) => (localCounts.get(number) ?? 0) > 0).length;
  return `홀짝 ${oddCount}:${6 - oddCount} · 합계 ${numbers.reduce((total, number) => total + number, 0)} · 최근 출현 ${recentHits}개 · 지역 이력 ${localHits}개`;
}

function strategyFor(method: RecommendationMethod): PickStrategy {
  switch (method) {
    case "BALANCED": return "BALANCED";
    case "RANDOM": return "UNIFORM";
    case "LOCAL_HOT": return "NEARBY";
    case "MIXED": return "MIXED";
    default: return "HOT";
  }
}

function weightsFor(
  method: RecommendationMethod,
  periodDraws: readonly PublishedDraw[],
  overall: readonly NumberFrequency[],
  local: readonly NumberFrequency[],
  recent: readonly NumberFrequency[],
): ReadonlyMap<number, number> {
  const allCounts = new Map(overall.map((item) => [item.number, item.count]));
  const localCounts = new Map(local.map((item) => [item.number, item.count]));
  const recentCounts = new Map(recent.map((item) => [item.number, item.count]));
  const allMax = Math.max(1, ...overall.map((item) => item.count));
  const localMax = Math.max(1, ...local.map((item) => item.count));
  const weights = new Map<number, number>();
  for (let number = 1; number <= 45; number += 1) {
    const all = allCounts.get(number) ?? 0;
    const nearby = localCounts.get(number) ?? 0;
    const recentCount = recentCounts.get(number) ?? 0;
    const lastSeenIndex = periodDraws.findIndex((draw) => draw.numbers.includes(number));
    const absence = lastSeenIndex < 0 ? periodDraws.length : lastSeenIndex;
    const value = method === "OVERALL_HOT" ? all
      : method === "LOCAL_HOT" ? nearby
        : method === "RECENT_SURGE" ? all + recentCount * 2
          : method === "LONG_ABSENT" ? absence * 10 + 1
            : method === "MIXED" ? Math.trunc(all * 50 / allMax) + Math.trunc(nearby * 50 / localMax)
              : 0;
    weights.set(number, value);
  }
  return weights;
}

function localDrawsFor(
  draws: readonly PublishedDraw[],
  events: readonly PublishedWinEvent[],
  storeIds: ReadonlySet<string>,
  ranks: ReadonlySet<number>,
): readonly PublishedDraw[] {
  const drawNos = new Set(events.filter((event) => ranks.has(event.rank) && storeIds.has(event.storeId)).map((event) => event.drawNo));
  return draws.filter((draw) => drawNos.has(draw.drawNo));
}

function storeIdsForScope(scope: RecommendationScope, stores: readonly LottoStore[], location: BrowserLocation | null, nearest: LottoStore | null): ReadonlySet<string> {
  if (scope === "NATIONWIDE") return new Set(stores.map((store) => store.id));
  if (location === null || nearest === null) return new Set();
  if (scope === "NEARBY_5") return new Set(stores.filter((store) => distanceKm(location, store) <= 5).map((store) => store.id));
  if (scope === "CURRENT_DISTRICT") return new Set(stores.filter((store) => store.region === nearest.region).map((store) => store.id));
  const province = nearest.region.trim().split(/\s+/)[0];
  return new Set(stores.filter((store) => store.region.startsWith(province ?? "")).map((store) => store.id));
}

function areaLabel(scope: RecommendationScope, nearest: LottoStore | null): string | null {
  if (scope === "NATIONWIDE") return "전국";
  if (nearest === null) return null;
  if (scope === "NEARBY_5") return "내 주변 5km";
  if (scope === "CURRENT_DISTRICT") return nearest.region;
  return nearest.region.trim().split(/\s+/)[0] ?? null;
}

function nearestStore(stores: readonly LottoStore[], location: BrowserLocation): LottoStore | null {
  return stores.reduce<LottoStore | null>((nearest, store) => nearest === null || distanceKm(location, store) < distanceKm(location, nearest) ? store : nearest, null);
}

function distanceKm(location: BrowserLocation, store: LottoStore): number {
  const rad = Math.PI / 180;
  const latitude = (store.latitude - location.latitude) * rad;
  const longitude = (store.longitude - location.longitude) * rad;
  const sineLatitude = Math.sin(latitude / 2);
  const sineLongitude = Math.sin(longitude / 2);
  const root = sineLatitude ** 2 + Math.cos(location.latitude * rad) * Math.cos(store.latitude * rad) * sineLongitude ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(root), Math.sqrt(1 - root));
}
